import type { Responsive } from '#types/common'
import type { ComponentCornersConfig } from '#types/config/components/cms/section'
import type {
  Content,
  ResponsiveRoundedCornerPreset,
  ResponsiveRoundedCorners,
  RoundedCornerPreset,
  RoundedCornersMode,
} from '#types/content'

type RoundedCornerBreakpoint = keyof Omit<Responsive, 'md'>

type Options = {
  force?: boolean
  enableWhen?: Omit<Responsive<boolean>, 'md'>
}

const isResponsiveRoundedCorners = (
  roundedCorners: Content['roundedCorners']
): roundedCorners is ResponsiveRoundedCorners => typeof roundedCorners === 'object' && roundedCorners !== null

const isResponsiveRoundedCornerPreset = (
  roundedCornerPreset: Content['roundedCornerPreset']
): roundedCornerPreset is ResponsiveRoundedCornerPreset => typeof roundedCornerPreset === 'object' && roundedCornerPreset !== null

const getRoundedCornerMode = (
  roundedCorners: Content['roundedCorners'],
  breakpoint: RoundedCornerBreakpoint
): RoundedCornersMode | undefined => isResponsiveRoundedCorners(roundedCorners)
  ? roundedCorners[breakpoint]
  : roundedCorners

const getRoundedCornerPreset = (
  roundedCornerPreset: Content['roundedCornerPreset'],
  breakpoint: RoundedCornerBreakpoint,
  config: ComponentCornersConfig,
  type: Content['type']
): RoundedCornerPreset => {
  const preset = isResponsiveRoundedCornerPreset(roundedCornerPreset)
    ? roundedCornerPreset[breakpoint]
    : roundedCornerPreset

  return preset || config.presetByType[type] || config.presetByType.default || 'default'
}

/**
 * Resolves the rounded-corner UnoCSS classes for a CMS content node, honouring its
 * `roundedCorners` mode (`'enabled'` / `'disabled'` / per-breakpoint overrides / inherited from side paddings).
 * Returns an empty string when no config is supplied or the node disables corners explicitly.
 * @param content - The CMS content node.
 * @param content.roundedCorners - Mode flag - `'enabled'`, `'disabled'`, a per-breakpoint override
 * object, or inherited from side paddings.
 * @param content.roundedCornerPreset - Optional preset override. Falls back to the config mapping.
 * @param content.componentSidePaddings - Per-breakpoint side-padding flags used when corners are inherited.
 * @param content.type - The CMS content type, used to look up the matching `config[type]` entry.
 * @param config - Preset classes plus a default preset mapping for each content type.
 * @param options - Optional overrides.
 * @param options.force - Apply rounded corners regardless of the inherited mode.
 * @param options.enableWhen - Per-breakpoint gating used when corners are inherited from side paddings.
 * @returns Either an array of class strings (when corners apply) or an empty string.
 * @category Utils
 */
export const getRoundedCorners = (
  { roundedCorners, roundedCornerPreset, componentSidePaddings, type }: Content,
  config: ComponentCornersConfig | undefined,
  { force, enableWhen }: Options = {}
) => {
  if (!config || roundedCorners === 'disabled') return ''

  return (Object.keys(config.presets.default) as RoundedCornerBreakpoint[])
    .flatMap((breakpoint) => {
      const mode = getRoundedCornerMode(roundedCorners, breakpoint)
      const preset = getRoundedCornerPreset(roundedCornerPreset, breakpoint, config, type)
      const value = config.presets[preset]?.[breakpoint] || config.presets.default[breakpoint]

      if (mode === 'disabled') return []
      if (!value) return []
      if (mode === 'enabled' || force) return [value]

      return (enableWhen?.[breakpoint] ?? true) && componentSidePaddings?.[breakpoint]
        ? [value]
        : []
    })
}
